import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  BarChart3, Heart, Repeat2, UserPlus, UserMinus, MessageCircle,
  Smartphone, TrendingUp, Clock, Activity, Target,
  Plus, Trash2, AlertCircle, Users
} from 'lucide-react';
import axios from 'axios';

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

const Page = styled.div`padding: 20px; display: flex; flex-direction: column; gap: 20px;`;

const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
`;
const Title = styled.h1`font-size: 22px; font-weight: 800; color: var(--text-primary); display: flex; align-items: center; gap: 10px;`;

const Badge = styled.span`
  padding: 4px 10px; border-radius: 12px; font-size: 11px; font-weight: 700;
  background: rgba(29,161,242,.12); color: #1da1f2;
`;

const TabRow = styled.div`
  display: flex; gap: 4px; padding: 4px; border-radius: 10px; background: var(--surface);
  width: fit-content;
`;

const Tab = styled.button`
  padding: 8px 20px; border-radius: 8px; border: none; cursor: pointer;
  font-size: 13px; font-weight: 700; transition: all .15s;
  background: ${p => p.$act ? '#1da1f2' : 'transparent'};
  color: ${p => p.$act ? '#fff' : 'var(--text-muted)'};
  display: flex; align-items: center; gap: 6px;
  &:hover { color: ${p => p.$act ? '#fff' : '#1da1f2'}; }
`;

const StatsGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 12px;
`;

const StatCard = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 12px;
  padding: 16px; text-align: center;
`;

const StatIcon = styled.div`
  width: 36px; height: 36px; border-radius: 10px; margin: 0 auto 8px;
  background: ${p => p.$color || '#1da1f2'}14;
  display: flex; align-items: center; justify-content: center;
  color: ${p => p.$color || '#1da1f2'};
`;

const StatValue = styled.div`font-size: 24px; font-weight: 800; color: var(--text-primary);`;
const StatLabel = styled.div`font-size: 11px; color: var(--text-muted); margin-top: 2px;`;

const Section = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 14px;
  padding: 20px;
  h3 { font-size: 15px; font-weight: 700; color: var(--text-primary); margin: 0 0 14px; display: flex; align-items: center; gap: 8px; }
`;

const Table = styled.table`
  width: 100%; border-collapse: collapse;
  th, td { padding: 8px 12px; text-align: left; font-size: 12px; border-bottom: 1px solid var(--card-border); }
  th { font-weight: 600; color: var(--text-muted); cursor: pointer; user-select: none; }
  th:hover { color: var(--text-primary); }
  td { color: var(--text-primary); }
  tr:hover td { background: var(--surface); }
`;

const NoDevice = styled.div`
  text-align: center; padding: 60px 20px; color: var(--text-muted);
  svg { margin-bottom: 12px; opacity: .4; }
  h3 { font-size: 16px; font-weight: 600; color: var(--text-primary); margin-bottom: 6px; }
`;

const EmptyState = styled.div`
  text-align: center; padding: 40px; color: var(--text-muted); font-size: 13px;
`;

/* ── Target-specific styles ── */

const AddTargetRow = styled.div`
  display: flex; gap: 8px; align-items: center; margin-bottom: 16px;
`;

const TargetInput = styled.input`
  flex: 1; padding: 10px 14px; border-radius: 10px;
  border: 1px solid var(--border); background: var(--input-bg, var(--surface));
  color: var(--text-primary); font-size: 13px; outline: none;
  &:focus { border-color: #1da1f2; }
  &::placeholder { color: var(--text-muted); }
`;

const AddBtn = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 10px 18px;
  border-radius: 10px; border: none; cursor: pointer;
  background: #1da1f2; color: #fff; font-size: 13px; font-weight: 700;
  transition: all .15s;
  &:hover { opacity: .85; transform: translateY(-1px); }
  &:disabled { opacity: .4; cursor: not-allowed; transform: none; }
`;

const DeleteBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; border-radius: 6px; border: none;
  background: transparent; color: var(--text-muted); cursor: pointer;
  &:hover { background: rgba(239,68,68,.12); color: #ef4444; }
`;

const StatusBadge = styled.span`
  padding: 2px 8px; border-radius: 6px; font-size: 10px; font-weight: 700;
  background: ${p => p.$active ? 'rgba(34,197,94,.12)' : 'rgba(245,158,11,.12)'};
  color: ${p => p.$active ? '#22c55e' : '#f59e0b'};
  cursor: pointer;
  &:hover { opacity: .8; }
`;

const ErrorMsg = styled.div`
  padding: 8px 14px; border-radius: 8px; font-size: 12px;
  background: rgba(239,68,68,.08); color: #ef4444;
  display: flex; align-items: center; gap: 6px; margin-bottom: 12px;
`;

const StatBadge = styled.span`
  display: inline-flex; align-items: center; gap: 3px; padding: 2px 6px;
  border-radius: 4px; font-size: 10px; font-weight: 700;
  background: ${p => p.$color}12; color: ${p => p.$color};
  font-family: 'JetBrains Mono', monospace;
`;

/* ═══════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════ */

const TwitterAnalytics = ({ selectedDevice }) => {
  const [data, setData] = useState({ sessions: [], dailyStats: [], recentActions: [] });
  const [, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('analytics'); // 'analytics' | 'targets'

  // Target state
  const [account, setAccount] = useState(null);
  const [targets, setTargets] = useState([]);
  const [newTarget, setNewTarget] = useState('');
  const [targetError, setTargetError] = useState(null);
  const [targetSort, setTargetSort] = useState({ key: 'name', dir: 'asc' });

  const deviceId = selectedDevice?.device_id;

  // Fetch account for this device
  const fetchAccount = useCallback(async () => {
    if (!deviceId) return;
    try {
      const r = await axios.get(`/api/twitter-scripts/account/${deviceId}`);
      setAccount(r.data.account || null);
    } catch (_) {}
  }, [deviceId]);

  // Fetch targets for account
  const fetchTargets = useCallback(async () => {
    if (!account?.id) return;
    try {
      const r = await axios.get(`/api/twitter-scripts/targets/${account.id}?purpose=interact`);
      setTargets(r.data.targets || []);
    } catch (_) {}
  }, [account?.id]);

  useEffect(() => {
    if (!deviceId) return;
    setLoading(true);
    fetchAccount();
    axios.get(`/api/twitter-scripts/analytics/${deviceId}`)
      .then(r => { if (r.data.success) setData(r.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [deviceId, fetchAccount]);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  // Target actions
  const addTarget = async () => {
    const val = newTarget.trim().replace(/^@/, '');
    if (!val || !account?.id) {
      setTargetError(!account?.id ? 'Configurez un compte Twitter d\'abord (page Compte)' : null);
      return;
    }
    try {
      const r = await axios.post('/api/twitter-scripts/targets', {
        accountId: account.id, username: val, type: 'user', purpose: 'interact'
      });
      if (r.data.success) {
        setNewTarget('');
        setTargetError(null);
        fetchTargets();
      } else {
        setTargetError(r.data.error || 'Erreur');
      }
    } catch (e) { setTargetError('Erreur réseau'); }
  };

  const removeTarget = async (targetId) => {
    try {
      await axios.delete(`/api/twitter-scripts/targets/${targetId}`);
      fetchTargets();
    } catch (_) {}
  };

  const toggleTargetStatus = async (targetId, currentStatus) => {
    const next = currentStatus === 'active' ? 'paused' : 'active';
    try {
      await axios.put(`/api/twitter-scripts/targets/${targetId}/status`, { status: next });
      fetchTargets();
    } catch (_) {}
  };

  const handleSort = (key) => {
    setTargetSort(prev => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'name' ? 'asc' : 'desc' }
    );
  };

  const sortedTargets = [...targets].sort((a, b) => {
    const dir = targetSort.dir === 'asc' ? 1 : -1;
    switch (targetSort.key) {
      case 'interactions': return ((a.total_interactions || 0) - (b.total_interactions || 0)) * dir;
      case 'users': return ((a.unique_users || 0) - (b.unique_users || 0)) * dir;
      case 'weight': return ((a.target_weight || 1) - (b.target_weight || 1)) * dir;
      case 'status': return (a.status || '').localeCompare(b.status || '') * dir;
      default: return (a.target_profile_name || '').localeCompare(b.target_profile_name || '') * dir;
    }
  });

  if (!deviceId) {
    return (
      <Page>
        <NoDevice>
          <Smartphone size={48} />
          <h3>Aucun appareil sélectionné</h3>
          <p>Sélectionnez un appareil pour voir les statistiques Twitter/X.</p>
        </NoDevice>
      </Page>
    );
  }

  // Aggregate totals from daily stats
  const totals = data.dailyStats.reduce((acc, d) => ({
    likes: acc.likes + (parseInt(d.total_likes) || 0),
    retweets: acc.retweets + (parseInt(d.total_retweets) || 0),
    follows: acc.follows + (parseInt(d.total_follows) || 0),
    unfollows: acc.unfollows + (parseInt(d.total_unfollows) || 0),
    dms: acc.dms + (parseInt(d.total_dms) || 0),
    sessions: acc.sessions + (parseInt(d.session_count) || 0),
  }), { likes: 0, retweets: 0, follows: 0, unfollows: 0, dms: 0, sessions: 0 });

  const activeTargets = targets.filter(t => t.status === 'active' || !t.status);
  const totalInteractions = targets.reduce((s, t) => s + (parseInt(t.total_interactions) || 0), 0);

  return (
    <Page>
      <Header>
        <Title><BarChart3 size={24} color="#1da1f2" /> Analytiques & Cibles Twitter/X</Title>
        <Badge>{account ? `@${account.twitter_handle}` : deviceId}</Badge>
      </Header>

      {/* Tab switcher */}
      <TabRow>
        <Tab $act={activeView === 'analytics'} onClick={() => setActiveView('analytics')}>
          <BarChart3 size={14} /> Analytiques
        </Tab>
        <Tab $act={activeView === 'targets'} onClick={() => setActiveView('targets')}>
          <Target size={14} /> Cibles ({targets.length})
        </Tab>
      </TabRow>

      {/* ═══════ ANALYTICS TAB ═══════ */}
      {activeView === 'analytics' && (
        <>
          <StatsGrid>
            <StatCard><StatIcon $color="#e0245e"><Heart size={18} /></StatIcon><StatValue>{totals.likes}</StatValue><StatLabel>Likes</StatLabel></StatCard>
            <StatCard><StatIcon $color="#17bf63"><Repeat2 size={18} /></StatIcon><StatValue>{totals.retweets}</StatValue><StatLabel>Retweets</StatLabel></StatCard>
            <StatCard><StatIcon $color="#794bc4"><UserPlus size={18} /></StatIcon><StatValue>{totals.follows}</StatValue><StatLabel>Follows</StatLabel></StatCard>
            <StatCard><StatIcon $color="#f59e0b"><UserMinus size={18} /></StatIcon><StatValue>{totals.unfollows}</StatValue><StatLabel>Unfollows</StatLabel></StatCard>
            <StatCard><StatIcon $color="#1da1f2"><MessageCircle size={18} /></StatIcon><StatValue>{totals.dms}</StatValue><StatLabel>DMs</StatLabel></StatCard>
            <StatCard><StatIcon $color="#6366f1"><Activity size={18} /></StatIcon><StatValue>{totals.sessions}</StatValue><StatLabel>Sessions</StatLabel></StatCard>
          </StatsGrid>

          <Section>
            <h3><Clock size={16} color="#1da1f2" /> Sessions récentes</h3>
            {data.sessions.length === 0 ? (
              <EmptyState>Aucune session Twitter enregistrée pour cet appareil.</EmptyState>
            ) : (
              <Table>
                <thead>
                  <tr><th>Date</th><th>Type</th><th>Likes</th><th>RT</th><th>Follows</th><th>DMs</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {data.sessions.map(s => (
                    <tr key={s.id}>
                      <td>{new Date(s.start_time).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                      <td>{s.session_type}</td>
                      <td>{s.total_likes || 0}</td>
                      <td>{s.total_retweets || 0}</td>
                      <td>{s.total_follows || 0}</td>
                      <td>{s.total_dms_sent || 0}</td>
                      <td style={{ color: s.stop_reason === 'success' ? '#22c55e' : s.stop_reason ? '#ef4444' : '#1da1f2' }}>{s.stop_reason || 'running'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Section>

          <Section>
            <h3><TrendingUp size={16} color="#1da1f2" /> Actions récentes</h3>
            {data.recentActions.length === 0 ? (
              <EmptyState>Aucune action Twitter enregistrée.</EmptyState>
            ) : (
              <Table>
                <thead>
                  <tr><th>Date</th><th>Action</th><th>Cible</th><th>Résultat</th></tr>
                </thead>
                <tbody>
                  {data.recentActions.slice(0, 30).map((a, i) => (
                    <tr key={i}>
                      <td>{new Date(a.timestamp).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                      <td>{a.action_type}</td>
                      <td>{a.target_handle || a.target_tweet_id || '—'}</td>
                      <td style={{ color: a.success ? '#22c55e' : '#ef4444' }}>{a.success ? '✓' : '✗'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Section>
        </>
      )}

      {/* ═══════ TARGETS TAB ═══════ */}
      {activeView === 'targets' && (
        <>
          <StatsGrid>
            <StatCard>
              <StatIcon $color="#1da1f2"><Target size={18} /></StatIcon>
              <StatValue>{targets.length}</StatValue>
              <StatLabel>Total cibles</StatLabel>
            </StatCard>
            <StatCard>
              <StatIcon $color="#22c55e"><Activity size={18} /></StatIcon>
              <StatValue>{activeTargets.length}</StatValue>
              <StatLabel>Cibles actives</StatLabel>
            </StatCard>
            <StatCard>
              <StatIcon $color="#a855f7"><TrendingUp size={18} /></StatIcon>
              <StatValue>{totalInteractions}</StatValue>
              <StatLabel>Interactions totales</StatLabel>
            </StatCard>
            <StatCard>
              <StatIcon $color="#3b82f6"><Users size={18} /></StatIcon>
              <StatValue>{targets.reduce((s, t) => s + (parseInt(t.unique_users) || 0), 0)}</StatValue>
              <StatLabel>Utilisateurs uniques</StatLabel>
            </StatCard>
          </StatsGrid>

          <Section>
            <h3><Target size={16} color="#1da1f2" /> Gestion des cibles</h3>

            {targetError && (
              <ErrorMsg><AlertCircle size={14} /> {targetError}</ErrorMsg>
            )}

            <AddTargetRow>
              <TargetInput
                placeholder="@username — ajouter une cible d'interaction"
                value={newTarget}
                onChange={e => setNewTarget(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addTarget(); }}
              />
              <AddBtn onClick={addTarget} disabled={!newTarget.trim() || !account?.id}>
                <Plus size={14} /> Ajouter
              </AddBtn>
            </AddTargetRow>

            {!account?.id && (
              <EmptyState>
                Aucun compte Twitter configuré pour cet appareil.<br />
                Allez dans la page <strong>Compte</strong> pour configurer un compte.
              </EmptyState>
            )}

            {account?.id && targets.length === 0 ? (
              <EmptyState>Aucune cible ajoutée. Ajoutez des comptes Twitter à cibler pour l'interaction automatique.</EmptyState>
            ) : account?.id && (
              <Table>
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')}>
                      Cible {targetSort.key === 'name' && (targetSort.dir === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('interactions')}>
                      Actions {targetSort.key === 'interactions' && (targetSort.dir === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('users')}>
                      Utilisateurs {targetSort.key === 'users' && (targetSort.dir === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Détails</th>
                    <th onClick={() => handleSort('weight')}>
                      Poids {targetSort.key === 'weight' && (targetSort.dir === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('status')}>
                      Status {targetSort.key === 'status' && (targetSort.dir === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Ajouté</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTargets.map(t => (
                    <tr key={t.id}>
                      <td style={{ fontWeight: 700 }}>@{t.target_profile_name}</td>
                      <td style={{ fontFamily: "'JetBrains Mono', monospace" }}>{t.total_interactions || 0}</td>
                      <td style={{ fontFamily: "'JetBrains Mono', monospace" }}>{t.unique_users || 0}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {(t.like_count > 0) && <StatBadge $color="#e0245e"><Heart size={9} /> {t.like_count}</StatBadge>}
                          {(t.follow_count > 0) && <StatBadge $color="#794bc4"><UserPlus size={9} /> {t.follow_count}</StatBadge>}
                        </div>
                      </td>
                      <td style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>{t.target_weight || 1}</td>
                      <td>
                        <StatusBadge
                          $active={t.status === 'active' || !t.status}
                          onClick={() => toggleTargetStatus(t.id, t.status || 'active')}
                        >
                          {t.status === 'active' || !t.status ? 'Actif' : 'Pausé'}
                        </StatusBadge>
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {t.created_at ? new Date(t.created_at).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td>
                        <DeleteBtn onClick={() => removeTarget(t.id)}>
                          <Trash2 size={13} />
                        </DeleteBtn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Section>
        </>
      )}
    </Page>
  );
};

export default TwitterAnalytics;
